import type { WaiterTable } from './waiterApi';

// SC-378 — configurable table ordering for the waiter tablet. Table numbers are
// alphanumeric (e.g. `12`, `A1`, `R4`), so a plain string sort yields the
// unusable 1,10,11,2,20,3 order. Two modes let the waiter pick an ordering that
// matches either the room layout or the live service:
//   - room:    by table number — pure numbers first (natural order), then
//              lettered tables. e.g. 1,2,3,4,A1,A2,A3,R1,R2
//   - service: open tables first, ordered by opening time (earliest→latest),
//              then the free tables in room order.
export type TableSortMode = 'room' | 'service';

export interface TableSortPref {
  mode: TableSortMode;
}

export const DEFAULT_TABLE_SORT: TableSortPref = { mode: 'room' };

const STORAGE_PREFIX = 'waiterTableSort:';

// Per-device sort preference, keyed by restaurant so a tablet dedicated to one
// room keeps its layout. Both helpers are guarded — localStorage can throw in
// private mode, in which case the sort just falls back to the default.
export function readTableSortPref(restaurantUuid: string): TableSortPref {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + restaurantUuid);
    if (!raw) return DEFAULT_TABLE_SORT;
    const parsed = JSON.parse(raw) as Partial<TableSortPref>;
    return { mode: parsed.mode === 'service' ? 'service' : 'room' };
  } catch {
    return DEFAULT_TABLE_SORT;
  }
}

export function writeTableSortPref(restaurantUuid: string, pref: TableSortPref): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + restaurantUuid, JSON.stringify(pref));
  } catch {
    // localStorage unavailable — persistence is a nicety, not required.
  }
}

// A table number is "numeric" only when it is pure digits (`12`), as opposed to
// carrying a letter prefix (`A1`, `R4`) or any other text.
// Exported: `noteSort.ts` (Emporter note labels) reuses the exact same
// numeric-first-then-natural ordering instead of re-declaring it.
export function isNumericTable(number: string | number): boolean {
  return /^\d+$/.test(String(number).trim());
}

// Natural comparison: split each string into alternating digit / non-digit
// chunks and compare chunk by chunk — digit chunks as integers (so `A2` < `A10`
// and `2` < `10`), text chunks case-insensitively (so `A*` < `R*`).
export function naturalCompare(a: string, b: string): number {
  const chunk = (s: string) => s.match(/\d+|\D+/g) ?? [];
  const ca = chunk(a);
  const cb = chunk(b);
  const len = Math.min(ca.length, cb.length);
  for (let i = 0; i < len; i++) {
    const pa = ca[i];
    const pb = cb[i];
    const na = /^\d/.test(pa);
    const nb = /^\d/.test(pb);
    if (na && nb) {
      const diff = parseInt(pa, 10) - parseInt(pb, 10);
      if (diff !== 0) return diff;
    } else {
      const diff = pa.localeCompare(pb, undefined, { sensitivity: 'base' });
      if (diff !== 0) return diff;
    }
  }
  return ca.length - cb.length;
}

// Room order: tables whose number starts with a digit before lettered ones,
// each group naturally sorted — a suffixed table stays beside its number
// (2, 2A, 3 … 10), it is not pushed after the last pure number.
function startsWithDigit(number: string | number): boolean {
  return /^\d/.test(String(number).trim());
}

function compareRoom(a: WaiterTable, b: WaiterTable): number {
  const aNumeric = startsWithDigit(a.number);
  const bNumeric = startsWithDigit(b.number);
  if (aNumeric !== bNumeric) return aNumeric ? -1 : 1;
  return naturalCompare(String(a.number), String(b.number));
}

// Service order: open (occupied) tables before free ones; open tables by
// opening time ascending, ties and free tables fall back to room order.
function compareService(a: WaiterTable, b: WaiterTable): number {
  const aOpen = a.session !== null;
  const bOpen = b.session !== null;
  if (aOpen !== bOpen) return aOpen ? -1 : 1;
  if (aOpen && bOpen) {
    const ta = Date.parse(a.session!.openedAt);
    const tb = Date.parse(b.session!.openedAt);
    if (ta !== tb) return ta - tb;
  }
  return compareRoom(a, b);
}

/**
 * Order a waiter's tables by the chosen mode. Returns a new array.
 */
export function sortWaiterTables(tables: WaiterTable[], mode: TableSortMode): WaiterTable[] {
  const compare = mode === 'service' ? compareService : compareRoom;
  return [...tables].sort(compare);
}

/**
 * SC-976 — tables réunies : chaque rattachée vient se ranger juste derrière son
 * ancre, dans l'ordre de `session.tableUuids`, pour que la tablée se lise d'un
 * bloc sur la grille au lieu d'être éparpillée (« S7 » perdue entre S6 et S8).
 * Une rattachée dont l'ancre n'est pas dans la liste (filtre de salle ou d'état)
 * garde sa place : elle n'a personne à suivre. Returns a new array.
 */
export function groupMergedTables(tables: WaiterTable[]): WaiterTable[] {
  const anchorSessions = new Set(
    tables.filter((tb) => tb.role === 'anchor' && tb.session?.isMerged).map((tb) => tb.session!.uuid),
  );
  const followsAnchor = (tb: WaiterTable) =>
    tb.role === 'member' && !!tb.session && anchorSessions.has(tb.session.uuid);
  const result: WaiterTable[] = [];
  for (const tb of tables) {
    if (followsAnchor(tb)) continue;
    result.push(tb);
    if (tb.role !== 'anchor' || !tb.session || !anchorSessions.has(tb.session.uuid)) continue;
    const order = tb.session.tableUuids ?? [];
    const rank = (uuid: string) => {
      const i = order.indexOf(uuid);
      return i === -1 ? order.length : i;
    };
    const members = tables.filter((m) => followsAnchor(m) && m.session!.uuid === tb.session!.uuid);
    result.push(...members.sort((a, b) => rank(a.uuid) - rank(b.uuid)));
  }
  return result;
}

/**
 * SC-976 — découpe une liste déjà passée par `groupMergedTables` en blocs : une
 * tablée réunie (ancre + rattachées qui la suivent) forme un seul bloc, toute
 * autre table un bloc d'une tuile. La grille pose chaque bloc d'un seul tenant.
 */
export function chunkTableGroups(tables: WaiterTable[]): WaiterTable[][] {
  const chunks: WaiterTable[][] = [];
  for (const tb of tables) {
    const last = chunks[chunks.length - 1];
    const joinsLast =
      tb.role === 'member' &&
      !!tb.session &&
      last?.[0]?.role === 'anchor' &&
      last[0].session?.uuid === tb.session.uuid;
    if (joinsLast) last.push(tb);
    else chunks.push([tb]);
  }
  return chunks;
}

/**
 * SC-976 — combien de colonnes de grille prend la carte d'une tablée, selon sa
 * taille et la largeur de la rangée : 2 tables aux numéros courts tiennent dans
 * une tuile normale (pas de trou en fin de rangée sur téléphone) ; jusqu'à 4
 * tables, 2 colonnes ; 5–6, 3 ; 7–8, 4 — jamais plus que la rangée n'en compte.
 */
export function groupColumnSpan(anchor: WaiterTable, members: WaiterTable[], gridColumns: number): number {
  if (members.length === 0) return 1;
  const numbers = [anchor, ...members].map((tb) => String(tb.number));
  const chars = numbers.reduce((sum, n) => sum + n.length, 0);
  const wanted = numbers.length === 2 && chars <= 6 ? 1 : numbers.length <= 4 ? 2 : numbers.length <= 6 ? 3 : 4;
  return Math.max(1, Math.min(wanted, gridColumns));
}

/**
 * SC-976 — colonnes de chaque bloc de la grille, dans l'ordre. Rejoue le
 * placement automatique ligne par ligne : une grande tablée (5 tables et plus)
 * qui ne tient pas dans le reste de la rangée se resserre sur les colonnes
 * restantes (au moins 2 ; 3 à partir de 7 tables, sinon ses puces ne tiennent
 * plus dans la hauteur d'une tuile) au lieu de laisser un trou.
 */
export function layoutChunkSpans(chunks: WaiterTable[][], gridColumns: number): number[] {
  let used = 0;
  return chunks.map((chunk) => {
    let span = groupColumnSpan(chunk[0], chunk.slice(1), gridColumns);
    const left = gridColumns - used;
    if (span > left && chunk.length >= 5 && left >= (chunk.length >= 7 ? 3 : 2)) span = left;
    if (span > left) used = 0;
    used = (used + span) % gridColumns;
    return span;
  });
}
